import {
  forecastYear,
  ELEMENT_CLASH,
  getStemName,
  getTopElementsFromStrength
} from '../utils/bazi';
import { CAREER_GUIDANCE } from '../bazi-engine/rules/career_rules';
import {
  buildConclusionParagraphs,
  buildDaiVanRows,
  buildPersonalityParagraphs,
  buildRelationshipDeepParagraphs,
  buildRelationshipParagraphs,
  buildRuleBasedParagraphs
} from '../bazi-engine/rules/reading_rules';
import { getCareerClustersByDominantElement } from '../utils/careerClusters';
import { formatLunarDateVerbose, getDayCanChi, getMonthCanChi, solarToLunar } from '../utils/lunarUtils';
import BAZI_TEXTS from '../components/baziTexts';
import {
  FAMILY_GUIDANCE,
  INVESTMENT_GUIDANCE,
  buildInvestmentGuidanceProfile
} from '../bazi-engine/rules/guidance_rules';
import { YEARLY_OUTLOOK_YEARS } from '../components/baziConfig';

const ELEMENT_LABELS = {
  vi: { 'Mộc': 'Mộc', 'Hỏa': 'Hỏa', 'Thổ': 'Thổ', 'Kim': 'Kim', 'Thủy': 'Thủy' },
  en: { 'Mộc': 'Wood', 'Hỏa': 'Fire', 'Thổ': 'Earth', 'Kim': 'Metal', 'Thủy': 'Water' }
};

const ENGINE_ELEMENT_MAP = {
  Moc: 'Mộc',
  Hoa: 'Hỏa',
  Tho: 'Thổ',
  Kim: 'Kim',
  Thuy: 'Thủy'
};

const LOCAL_ELEMENT_TO_ENGINE = Object.fromEntries(
  Object.entries(ENGINE_ELEMENT_MAP).map(([engine, local]) => [local, engine])
);

const CONSTITUTION_COPY = {
  vi: {
    strong: 'Thân Mạnh 💪',
    weak: 'Thân Yếu 😔',
    balanced: 'Bình Hòa ⚖️'
  },
  en: {
    strong: 'Strong Body 💪',
    weak: 'Weak Body 😔',
    balanced: 'Balanced ⚖️'
  }
};

const ELEMENT_GENERATES = {
  'Mộc': 'Hỏa',
  'Hỏa': 'Thổ',
  'Thổ': 'Kim',
  'Kim': 'Thủy',
  'Thủy': 'Mộc'
};


const getElementController = (element) => (
  Object.entries(ELEMENT_CLASH).find(([, target]) => target === element)?.[0] || element
);

const getElementGenerator = (element) => (
  Object.entries(ELEMENT_GENERATES).find(([, target]) => target === element)?.[0] || element
);

const getGuidanceByElement = (guidanceMap, element, language) => {
  const localized = guidanceMap[language] || guidanceMap.vi || {};
  return localized[element] || null;
};

const DAI_VAN_WARNING_LABELS = {
  vi: {
    solar_term_next_wrapped_year: 'Tiết khí kế tiếp nằm ở năm dương lịch tiếp theo (wrapped year).',
    missing_prev_next_solar_terms: 'Thiếu dữ liệu tiết khí liền trước/liền sau để tính khởi vận.',
    cannot_determine_direction: 'Chưa xác định được chiều vận (kiểm tra giới tính và can năm).',
    cannot_compute_start_age: 'Không tính được tuổi khởi vận (thiếu tiết khí hoặc giờ sinh).',
    start_age_zero_suspect: 'Tuổi khởi vận = 0 (cần kiểm tra dữ liệu).',
    next_term_not_after_birth: 'Tiết khí kế tiếp không sau thời điểm sinh.',
    prev_term_not_before_birth: 'Tiết khí liền trước không đứng trước thời điểm sinh.',
    missing_gender: 'Thiếu giới tính để xác định chiều vận.'
  },
  en: {
    solar_term_next_wrapped_year: 'The next solar term falls in the following calendar year (wrapped year).',
    missing_prev_next_solar_terms: 'Missing adjacent solar terms to compute start age.',
    cannot_determine_direction: 'Cannot determine cycle direction (check gender and year stem).',
    cannot_compute_start_age: 'Cannot compute start age (missing solar term or birth time).',
    start_age_zero_suspect: 'Start age = 0 (data likely incomplete).',
    next_term_not_after_birth: 'Next solar term is not after the birth time.',
    prev_term_not_before_birth: 'Previous solar term is not before the birth time.',
    missing_gender: 'Missing gender to determine direction.'
  }
};

const formatDate = (date) => (
  date instanceof Date && !Number.isNaN(date.getTime())
    ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
    : ''
);

const formatHourRange = (hour) => (
  Number.isFinite(hour) ? `${String(hour).padStart(2, '0')}:00–${String(hour).padStart(2, '0')}:59` : ''
);

const getAgeFromBirthDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const beforeBirthday = today.getMonth() < date.getMonth()
    || (today.getMonth() === date.getMonth() && today.getDate() < date.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

const getElementStrengthRows = (analysis, language) => (
  Object.entries(analysis.strength).map(([element, value]) => {
    const percent = Number(analysis.balance[element] || 0);
    return [
      getElementLabel(element, language),
      `${value.toFixed(2)} (${percent.toFixed(1)}%)`
    ];
  })
);

const getDominantElements = (analysis) => (
  getTopElementsFromStrength(analysis?.strength)
);

const getWeakestElement = (analysis) => (
  Object.entries(analysis.strength).sort(([, a], [, b]) => a - b)[0]?.[0]
);

const normalizeDaiVanMeta = (meta) => {
  if (!meta) return null;
  const directionValue = meta.direction?.value || meta.direction || null;
  const startAge = Number.isFinite(meta.start_age?.value)
    ? meta.start_age.value
    : Number.isFinite(meta.start_age)
      ? meta.start_age
      : Number.isFinite(meta.startAge)
        ? meta.startAge
        : null;
  return {
    direction: directionValue,
    startAge,
    quality: meta.quality || null,
    raw: meta
  };
};

const formatRangeValue = (value) => {
  if (!Number.isFinite(value)) return null;
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toString();
};

const normalizeDaiVanPeriod = (period) => {
  if (!period) return null;
  const can = period.pillar?.can || period.can || period.canChi?.can || null;
  const chi = period.pillar?.chi || period.chi || period.canChi?.chi || null;
  const label = period.pillar?.label || period.label || period.canChi?.label || (can && chi ? `${can}-${chi}` : null);
  const canIndex = Number.isInteger(period.pillar?.canIndex)
    ? period.pillar.canIndex
    : period.canChi?.canIndex;
  const chiIndex = Number.isInteger(period.pillar?.chiIndex)
    ? period.pillar.chiIndex
    : period.canChi?.chiIndex;

  const ageFrom = period.age_from ?? period.ageFrom;
  const ageTo = period.age_to ?? period.ageTo;
  const yearFrom = period.year_from ?? period.yearFrom;
  const yearTo = period.year_to ?? period.yearTo
    ?? (Number.isFinite(period.year_to_exclusive) ? period.year_to_exclusive - 1 : undefined);

  const ageRange = period.ageRange
    || (formatRangeValue(ageFrom) && formatRangeValue(ageTo)
      ? `${formatRangeValue(ageFrom)}-${formatRangeValue(ageTo)}`
      : null);
  const yearRangeExclusive = period.year_range_exclusive ?? period.yearRangeExclusive;
  const yearRange = period.yearRange
    || period.year_range
    || (Number.isFinite(yearFrom) && Number.isFinite(yearTo)
      ? `${yearFrom}-${yearTo}`
      : null);

  const canChi = can || chi || label
    ? {
        can,
        chi,
        label,
        canIndex,
        chiIndex,
        canChi: period.canChi
      }
    : null;

  return {
    ...period,
    canChi,
    ageRange,
    yearRange,
    yearRangeExclusive
  };
};

const normalizeDaiVanPeriods = (periods) => (
  Array.isArray(periods) ? periods.map(normalizeDaiVanPeriod).filter(Boolean) : []
);

const getDaiVanDataFromEngine = (bazi) => {
  const dataset = bazi?.engineReport?.dai_van_dataset
    || bazi?.engineReport?.fortune?.dai_van_dataset;
  const meta = bazi?.engineReport?.dai_van_meta
    || bazi?.engineReport?.fortune?.dai_van_meta
    || dataset?.meta
    || null;

  if (dataset?.periods?.length) {
    return {
      periods: normalizeDaiVanPeriods(dataset.periods),
      meta,
      dataset
    };
  }

  const daiVanRaw = bazi?.engineReport?.dai_van
    || bazi?.engineReport?.fortune?.dai_van;
  const periodCandidates = [
    daiVanRaw?.periods,
    daiVanRaw,
    bazi?.engineReport?.fortune?.dai_van?.periods,
    bazi?.engineReport?.fortune?.dai_van
  ];
  const periods = normalizeDaiVanPeriods(periodCandidates.find(Array.isArray) || []);

  return { periods, meta, dataset: dataset || null };
};

const buildDaiVanRowsFromEngine = ({ bazi, language, labelKey, periods }) => {
  const daiVanData = periods || getDaiVanDataFromEngine(bazi).periods;
  const daiVanPeriods = Array.isArray(daiVanData) ? daiVanData : [];

  const periodsToUse = daiVanPeriods.length ? daiVanPeriods : [];
  if (!periodsToUse.length) return [];
  const dungThan = bazi?.engineReport?.dung_than || [];
  const hyThan = bazi?.engineReport?.hy_than || [];
  const kyThan = bazi?.engineReport?.ky_than || [];

  return buildDaiVanRows({
    periods: periodsToUse,
    dungThan,
    hyThan,
    kyThan,
    language,
    labelKey,
    formatElementLabel: (elementKey) => (
      getElementLabel(
        normalizeEngineElement(elementKey),
        language
      )
    )
  });
};

const buildDaiVanMetaParagraphs = ({ bazi, language, copy, hasRows = false }) => {
  const { meta } = getDaiVanDataFromEngine(bazi);
  const normalizedMeta = normalizeDaiVanMeta(meta);
  const metaCopy = copy?.meta || {};
  if (!normalizedMeta) {
    return hasRows || !metaCopy.missing ? [] : [metaCopy.missing];
  }

  const directionRaw = normalizedMeta.direction;
  const directionKey = directionRaw === 'nghich' || directionRaw === 'backward' ? 'nghich' : 'thuan';
  const directionLabel = metaCopy.direction?.[directionKey];
  const directionRule = metaCopy.directionRule;
  const startAgeValue = normalizedMeta.startAge;
  const startAgeLine = Number.isFinite(startAgeValue) && typeof metaCopy.startAge === 'function'
    ? metaCopy.startAge(startAgeValue)
    : null;
  const startRule = metaCopy.startRule;
  const warningsRaw = normalizedMeta.quality?.warnings || [];
  const warningLabels = warningsRaw.map(
    (w) => DAI_VAN_WARNING_LABELS[language]?.[w]
      || DAI_VAN_WARNING_LABELS.vi?.[w]
      || w
  );
  const warningLine = warningLabels.length && typeof metaCopy.warning === 'function'
    ? metaCopy.warning(warningLabels)
    : null;

  const paragraphs = [directionLabel, directionRule, startAgeLine, startRule, warningLine]
    .filter(Boolean);

  return paragraphs.length ? paragraphs : (metaCopy.missing ? [metaCopy.missing] : []);
};

const buildYearOutlookRows = (bazi, language) => (
  Array.from({ length: YEARLY_OUTLOOK_YEARS }, (_, i) => {
    const year = 2025 + i;
    const forecast = forecastYear(bazi, year, language);
    return [
      String(year),
      forecast.yearPillar,
      getElementLabel(forecast.yearElement, language),
      `${forecast.relation} · ${forecast.outlook}`
    ];
  })
);

export const buildDetailedReading = ({
  bazi,
  analysis,
  birthDate,
  birthHour,
  language,
  timezoneOffsetMinutes,
  gender
}) => {
  const t = BAZI_TEXTS[language] || BAZI_TEXTS.vi;
  const copy = t.detailedReading || BAZI_TEXTS.vi.detailedReading;
  const dayMasterLabel = bazi.dayMasterLabelLocalized?.[language] || getStemName(bazi.dayMaster, language);
  const dayMasterElementLabel = getElementLabel(bazi.dayMasterElement, language);
  const age = getAgeFromBirthDate(birthDate);
  const timezoneOffsetHours = Number.isFinite(timezoneOffsetMinutes)
    ? timezoneOffsetMinutes / 60
    : 7;
  const lunarDate = birthDate ? solarToLunar(birthDate, timezoneOffsetHours) : null;
  const lunarDayCanChi = birthDate ? getDayCanChi(birthDate) : null;
  const lunarMonthCanChi = lunarDate ? getMonthCanChi(lunarDate.month, lunarDate.year) : null;
  const lunarYearCanChiLabel = lunarDate?.canChiYearLocalized?.[language] || lunarDate?.canChiYear;
  const lunarDayCanChiLabel = lunarDayCanChi
    ? (language === 'en' ? lunarDayCanChi.labelEn : lunarDayCanChi.label)
    : null;
  const lunarMonthCanChiLabel = lunarMonthCanChi
    ? (language === 'en' ? lunarMonthCanChi.labelEn : lunarMonthCanChi.label)
    : null;
  const dominantElements = getDominantElements(analysis);
  const dominantElement = dominantElements[0] || null;
  const weakestElement = getWeakestElement(analysis);
  const controllerElement = getElementController(bazi.dayMasterElement);
  const generatorElement = getElementGenerator(bazi.dayMasterElement);
  const controllerLabel = getElementLabel(controllerElement, language);
  const generatorLabel = getElementLabel(generatorElement, language);
  const dungThanLabel = formatElementList(bazi.engineReport?.dung_than, language) || controllerLabel;
  const hyThanLabel = formatElementList(bazi.engineReport?.hy_than, language) || generatorLabel;
  const kyThanLabel = formatElementList(bazi.engineReport?.ky_than, language);
  const tabooOverlapsWithSupport = kyThanLabel
    ? [dungThanLabel, hyThanLabel].includes(kyThanLabel)
    : false;
  const dungThanReasoning = bazi.engineReport?.dung_than_reasoning || [];
  const dungThanReasoningText = formatReasoningDetails(dungThanReasoning);
  const dominantLabel = formatDominantElementLabel(dominantElements, language);
  const weakLabel = weakestElement ? getElementLabel(weakestElement, language) : '';
  const constitutionText = getConstitutionText(analysis.constitutionKey, language, analysis.constitution);
  const careerGuidance = getGuidanceByElement(CAREER_GUIDANCE, dominantElement || bazi.dayMasterElement, language);
  const careerClusters = getCareerClustersByDominantElement(dominantElement, language);
  const careerClusterBlocks = careerClusters.map((cluster) => {
    const topRoles = cluster.roles.slice(0, 6).map((role) => role.name);
    const roleLine = language === 'en'
      ? `Key roles: ${topRoles.join(', ')}`
      : `Vai trò tiêu biểu: ${topRoles.join(', ')}`;

    return {
      label: cluster.title,
      body: [cluster.summary, roleLine]
    };
  });
  const investmentGuidance = buildInvestmentGuidanceProfile(bazi.engineReport || {}, language)
    || getGuidanceByElement(INVESTMENT_GUIDANCE, dominantElement || bazi.dayMasterElement, language);
  const familyGuidance = getGuidanceByElement(FAMILY_GUIDANCE, bazi.dayMasterElement, language);
  const healthReading = bazi.engineReport?.luan_doan?.suc_khoe;
  const careerReading = bazi.engineReport?.luan_doan?.su_nghiep;
  const wealthReading = bazi.engineReport?.luan_doan?.tai_van;
  const personalityElementKey = bazi.engineReport?.nhat_chu?.ngu_hanh
    || LOCAL_ELEMENT_TO_ENGINE[bazi.dayMasterElement];
  const fiveElementsMeta = bazi.engineReport?.five_elements_meta || null;
  const fiveElementsMethodText = fiveElementsMeta && t.fiveElementsMethod
    ? t.fiveElementsMethod(fiveElementsMeta)
    : null;
  const personalityParagraphs = buildPersonalityParagraphs({
    dayMasterLabel,
    elementLabel: dayMasterElementLabel,
    dayMasterElement: personalityElementKey,
    language
  });
  const careerReadingParagraphs = buildRuleBasedParagraphs({
    reading: careerReading,
    language
  });
  const wealthReadingParagraphs = buildRuleBasedParagraphs({
    reading: wealthReading,
    language
  });
  const healthReadingParagraphs = buildRuleBasedParagraphs({
    reading: healthReading,
    language
  });
  const relationshipParagraphs = buildRelationshipParagraphs({
    guidance: familyGuidance,
    quanHe: bazi.engineReport?.quan_he,
    language
  });
  const relationshipDeepParagraphs = buildRelationshipDeepParagraphs({
    quanHe: bazi.engineReport?.quan_he,
    language
  });
  const conclusionParagraphs = buildConclusionParagraphs({
    healthReading,
    careerReading,
    wealthReading,
    language
  });
  const tabooLines = kyThanLabel
    ? [
      language === 'en'
        ? (tabooOverlapsWithSupport
          ? `${kyThanLabel} is a helper element here—use it moderately to vent without letting it dominate.`
          : `Avoid over-boosting ${kyThanLabel} once balance is reached; if it is currently weak, it is not the primary risk.`)
        : (tabooOverlapsWithSupport
          ? `${kyThanLabel} là dụng thần phụ, dùng vừa đủ để xả mà không đẩy hành này quá vượng.`
          : `Không khuyến khích làm ${kyThanLabel} tăng quá mạnh khi tổng thể đã cân bằng; hiện tại ${kyThanLabel} yếu thì chưa phải rủi ro chính.`),
      language === 'en'
        ? 'Maintain steady routines and avoid over-amplifying a single element.'
        : 'Giữ nhịp sinh hoạt ổn định và tránh khuếch đại một hành quá mức.'
    ]
    : [
      copy.sections.harmony.taboo.default.first,
      copy.sections.harmony.taboo.default.second
    ];
  const yearLabel = bazi.pillars.year.canChiLabelLocalized?.[language] || `${bazi.pillars.year.canName}-${bazi.pillars.year.chiName}`;
  const monthLabel = bazi.pillars.month.canChiLabelLocalized?.[language] || `${bazi.pillars.month.canName}-${bazi.pillars.month.chiName}`;
  const dayLabel = bazi.pillars.day.canChiLabelLocalized?.[language] || `${bazi.pillars.day.canName}-${bazi.pillars.day.chiName}`;
  const hourLabel = bazi.pillars.hour.canChiLabelLocalized?.[language] || `${bazi.pillars.hour.canName}-${bazi.pillars.hour.chiName}`;
  const birthDateLabel = formatDate(birthDate);
  const hourRangeLabel = formatHourRange(birthHour);
  const lunarIntroLine = lunarDate
    ? copy.intro.lunarDate(
      formatLunarDateVerbose(lunarDate, language),
      lunarDayCanChiLabel,
      lunarMonthCanChiLabel,
      lunarYearCanChiLabel
    )
    : null;
  const careerSummary = careerGuidance?.summary || copy.sections.career.summary;
  const careerFit = careerGuidance?.fit || copy.sections.career.blocks.fit;
  const careerAvoid = careerGuidance?.avoid || copy.sections.career.blocks.avoid;
  const investmentProfile = investmentGuidance?.profile || null;
  const investmentSummary = investmentGuidance?.summary || copy.sections.investment.summary;
  const investmentProfileLine = investmentProfile
    ? (language === 'en'
      ? `Profile tendency: ${investmentProfile.styleTags.join(', ')} (discipline score ${investmentProfile.score}/10).`
      : `Có xu hướng: ${investmentProfile.styleTags.join(', ')} (điểm kỷ luật ${investmentProfile.score}/10).`)
    : null;
  const investmentDisciplineLine = investmentGuidance?.discipline?.length
    ? (language === 'en'
      ? `Discipline anchors: ${investmentGuidance.discipline.join('; ')}.`
      : `Kỷ luật nên duy trì: ${investmentGuidance.discipline.join('; ')}.`)
    : null;
  const investmentAllocationLine = investmentProfile?.allocations
    ? (language === 'en'
      ? `Behavioral allocation hint: cash ${investmentProfile.allocations.cashPctRange}, stocks ${investmentProfile.allocations.stockPctRange}, real estate ${investmentProfile.allocations.realEstatePctRange}.`
      : `Gợi ý phân bổ hành vi: tiền mặt ${investmentProfile.allocations.cashPctRange}, cổ phiếu ${investmentProfile.allocations.stockPctRange}, BĐS ${investmentProfile.allocations.realEstatePctRange}.`)
    : null;
  const investmentFit = investmentGuidance?.fit || copy.sections.investment.blocks.fit;
  const investmentAvoid = investmentGuidance?.avoid || copy.sections.investment.blocks.avoid;
  const careerParagraphs = [
    ...careerReadingParagraphs,
    careerSummary
  ].filter(Boolean);
  const personalityFallback = copy.sections.personality?.paragraphs
    ? [
      copy.sections.personality.paragraphs.first(dayMasterLabel, dayMasterElementLabel),
      copy.sections.personality.paragraphs.second(dominantLabel || '—'),
      copy.sections.personality.paragraphs.third(weakLabel || '—'),
      copy.sections.personality.paragraphs.fourth
    ]
    : [];
  const personalityNarrative = personalityParagraphs.length
    ? personalityParagraphs
    : personalityFallback;
  const destinyRows = buildDaiVanRowsFromEngine({
    bazi,
    language,
    labelKey: 'ageRange'
  });
  const decadeRows = buildDaiVanRowsFromEngine({
    bazi,
    language,
    labelKey: 'yearRange'
  });
  const daiVanParagraphs = buildDaiVanMetaParagraphs({
    bazi,
    language,
    copy: copy.sections.destiny,
    hasRows: destinyRows.length > 0
  });
  const familyParagraphs = relationshipParagraphs.length
    ? relationshipParagraphs
    : copy.sections.family.paragraphs || [];
  const wealthParagraphs = [
    ...(wealthReadingParagraphs.length
      ? wealthReadingParagraphs
      : (copy.sections.wealth.paragraphs || [])),
    language === 'en'
      ? `Balance by favoring ${dungThanLabel} and ${hyThanLabel} themes to stabilize finances.`
      : `Gợi ý cân bằng: ưu tiên yếu tố ${dungThanLabel} và ${hyThanLabel} để ổn định tài vận.`
  ].filter(Boolean);
  const healthParagraphs = healthReadingParagraphs.length
    ? healthReadingParagraphs
    : [
      copy.sections.health.paragraphs.first(weakLabel || '—'),
      copy.sections.health.paragraphs.second,
      copy.sections.health.paragraphs.third
    ].filter(Boolean);
  const dungThanReasoningLine = dungThanReasoningText
    ? copy.sections.harmony.blocks.controllerBody.reasoning(dungThanReasoningText)
    : null;

  const balanceCalloutLines = [
    fiveElementsMethodText,
    copy.sections.balance.dominant(dominantLabel || '—'),
    copy.sections.balance.weakest(weakLabel || '—')
  ].filter(Boolean);

  const harmonyCalloutLines = analysis.recommendation
    ? [analysis.recommendation]
    : [];

  const sections = [
    {
      id: 'pillars',
      title: copy.sections.pillars.title,
      paragraphs: [copy.sections.pillars.description],
      blocks: [
        {
          label: copy.sections.pillars.blocks.year,
          body: [
            yearLabel,
            copy.sections.pillars.elementLine(getElementLabel(bazi.pillars.year.element, language))
          ]
        },
        {
          label: copy.sections.pillars.blocks.month,
          body: [
            monthLabel,
            copy.sections.pillars.elementLine(getElementLabel(bazi.pillars.month.element, language))
          ]
        },
        {
          label: copy.sections.pillars.blocks.day,
          body: [
            dayLabel,
            copy.sections.pillars.dayMasterLine(dayMasterLabel, dayMasterElementLabel),
            copy.sections.pillars.dayBranchLine(getElementLabel(bazi.pillars.day.chiElement, language))
          ]
        },
        {
          label: copy.sections.pillars.blocks.hour,
          body: [
            hourLabel,
            copy.sections.pillars.elementLine(getElementLabel(bazi.pillars.hour.element, language))
          ]
        }
      ],
      summary: [
        copy.sections.pillars.summary.year(yearLabel, getElementLabel(bazi.pillars.year.element, language)),
        copy.sections.pillars.summary.month(monthLabel, getElementLabel(bazi.pillars.month.element, language)),
        copy.sections.pillars.summary.day(dayLabel, dayMasterElementLabel),
        copy.sections.pillars.summary.hour(hourLabel, getElementLabel(bazi.pillars.hour.element, language))
      ]
    },
    {
      id: 'balance',
      title: copy.sections.balance.title,
      table: analysis.strength ? {
        headers: copy.sections.balance.tableHeaders,
        rows: getElementStrengthRows(analysis, language)
      } : null,
      callout: balanceCalloutLines
    },
    {
      id: 'harmony',
      title: copy.sections.harmony.title,
      paragraphs: [
        copy.sections.harmony.blocks.controllerLabel(dungThanLabel),
        copy.sections.harmony.blocks.controllerBody.first(constitutionText),
        copy.sections.harmony.blocks.controllerBody.second(dungThanLabel, dayMasterElementLabel),
        dungThanReasoningLine,
        copy.sections.harmony.blocks.generatorLabel(hyThanLabel),
        copy.sections.harmony.blocks.generatorBody.first(hyThanLabel, dayMasterElementLabel),
        copy.sections.harmony.blocks.generatorBody.second(hyThanLabel)
      ].filter(Boolean),
      blocks: [{
        label: copy.sections.harmony.blocks.tabooLabel,
        body: tabooLines
      }],
      callout: harmonyCalloutLines
    },
    {
      id: 'personality',
      title: copy.sections.personality.title,
      paragraphs: personalityNarrative
    },
    {
      id: 'career',
      title: copy.sections.career.title,
      blocks: [
        {
          label: copy.sections.career.blocks.fitLabel,
          body: careerFit
        },
        {
          label: copy.sections.career.blocks.avoidLabel,
          body: careerAvoid
        }
      ],
      paragraphs: careerParagraphs
    },
    ...(careerClusterBlocks.length > 0 ? [{
      id: 'careerClusters',
      title: copy.sections.careerClusters.title,
      paragraphs: [copy.sections.careerClusters.description(dominantLabel || '—')],
      blocks: careerClusterBlocks
    }] : []),
    {
      id: 'wealth',
      title: copy.sections.wealth.title,
      paragraphs: wealthParagraphs
    },
    {
      id: 'family',
      title: copy.sections.family.title,
      paragraphs: familyParagraphs
    },
    {
      id: 'health',
      title: copy.sections.health.title,
      paragraphs: healthParagraphs
    },
    {
      id: 'destiny',
      title: copy.sections.destiny.title,
      paragraphs: daiVanParagraphs,
      table: destinyRows.length ? {
        headers: copy.sections.destiny.tableHeaders,
        rows: destinyRows
      } : null
    },
    {
      id: 'decades',
      title: copy.sections.decades.title,
      paragraphs: decadeRows.length ? [] : daiVanParagraphs,
      table: decadeRows.length ? {
        headers: copy.sections.decades.tableHeaders,
        rows: decadeRows
      } : null
    },
    {
      id: 'yearly',
      title: copy.sections.yearly.title,
      paragraphs: [
        copy.sections.yearly.paragraphs.first,
        copy.sections.yearly.paragraphs.second(dungThanLabel, hyThanLabel),
        copy.sections.yearly.paragraphs.third,
        copy.sections.yearly.paragraphs.fourth
      ],
      table: {
        headers: copy.sections.yearly.tableHeaders,
        rows: buildYearOutlookRows(bazi, language)
      }
    },
    {
      id: 'investment',
      title: copy.sections.investment.title,
      blocks: [
        {
          label: copy.sections.investment.blocks.fitLabel,
          body: investmentFit
        },
        {
          label: copy.sections.investment.blocks.avoidLabel,
          body: investmentAvoid
        }
      ],
      paragraphs: [
        investmentSummary,
        investmentProfileLine,
        investmentAllocationLine,
        investmentDisciplineLine
      ].filter(Boolean)
    },
    {
      id: 'familyDeep',
      title: copy.sections.familyDeep.title,
      paragraphs: relationshipDeepParagraphs.length
        ? relationshipDeepParagraphs
        : copy.sections.familyDeep.paragraphs
    },
    {
      id: 'conclusion',
      title: copy.sections.conclusion.title,
      paragraphs: conclusionParagraphs.length
        ? conclusionParagraphs
        : copy.sections.conclusion.paragraphs
    }
  ];

  const metaAssumptions = bazi?.engineReport?.meta?.assumptions || [];
  const metaDisclaimer = bazi?.engineReport?.meta?.disclaimer
    || 'Kết quả chỉ mang tính tham khảo/học thuật/giải trí.';
  const metaTimezone = Number.isFinite(timezoneOffsetMinutes)
    ? timezoneOffsetMinutes
    : 420;

  const detailedDatasetPayload = {
    meta: {
      ...(bazi?.engineReport?.meta || {}),
      timezoneOffsetMinutes: metaTimezone,
      assumptions: metaAssumptions,
      disclaimer: metaDisclaimer,
      dayPillarTrace: bazi?.engineReport?.four_pillars?.day?.meta
        || bazi?.engineReport?.pillars?.day?.meta
        || null
    },
    input: {
      birthDate: birthDateLabel,
      birthHour,
      timezoneOffsetMinutes,
      gender,
      lunarDate: lunarDate ? {
        day: lunarDate.day,
        month: lunarDate.month,
        year: lunarDate.year,
        leap: lunarDate.leap,
        canChiDay: lunarDayCanChi ? {
          vi: lunarDayCanChi.label,
          en: lunarDayCanChi.labelEn
        } : null,
        canChiMonth: lunarMonthCanChi ? {
          vi: lunarMonthCanChi.label,
          en: lunarMonthCanChi.labelEn
        } : null,
        canChiYear: lunarDate.canChiYear ? {
          vi: lunarDate.canChiYear,
          en: lunarDate.canChiYearLocalized?.en
        } : null
      } : null
    },
    pillars: bazi?.pillars,
    analysis: analysis ? {
      constitution: analysis.constitution,
      constitutionKey: analysis.constitutionKey,
      relativeStrength: analysis.relativeStrength,
      recommendationKey: analysis.recommendationKey,
      strength: analysis.strength,
      balance: analysis.balance,
      balanceText: analysis.balanceText
    } : null,
    sections
  };

  const detailedDatasetJson = JSON.stringify(detailedDatasetPayload, null, 2);
  const detailedDatasetBlock = detailedDatasetJson
    ? {
        label: copy.dataset?.label,
        help: copy.dataset?.help,
        copyCta: copy.dataset?.copyCta,
        copied: copy.dataset?.copied,
        failed: copy.dataset?.failed,
        data: detailedDatasetJson
      }
    : null;

  return {
    title: t.detailedReadingTitle,
    intro: [
      copy.intro.birthDate(birthDateLabel || '—'),
      lunarIntroLine,
      copy.intro.birthHour(hourRangeLabel || '—'),
      copy.intro.age(yearLabel, age)
    ].filter(Boolean),
    dataset: detailedDatasetBlock,
    sections
  };
};



const getElementLabel = (element, language = 'vi') => (
  ELEMENT_LABELS[language]?.[element] || element
);

const normalizeEngineElement = (element) => (
  ENGINE_ELEMENT_MAP[element] || element
);

const formatElementList = (elements, language) => {
  if (!elements || elements.length === 0) return '';
  return elements
    .map((element) => getElementLabel(normalizeEngineElement(element), language))
    .filter(Boolean)
    .join(', ');
};

const formatDominantElementLabel = (elements, language) => {
  if (!elements?.length) return '';
  const labels = elements
    .map((element) => getElementLabel(element, language))
    .filter(Boolean);
  if (!labels.length) return '';
  if (labels.length === 1) return labels[0];
  const suffix = language === 'en' ? 'co-dominant' : 'đồng vượng';
  return `${labels.join(' & ')} (${suffix})`;
};

const formatReasoningDetails = (items = []) => {
  if (!Array.isArray(items)) return null;
  const formatted = items
    .map((item) => {
      const factor = item?.label || item?.factor;
      const value = item?.value ?? item?.detail ?? item?.computed;
      if (factor == null || value == null) return null;
      return `${factor}=${value}`;
    })
    .filter(Boolean);
  return formatted.length ? formatted.join(', ') : null;
};

const getConstitutionText = (key, language, fallback) => (
  CONSTITUTION_COPY[language]?.[key] || fallback
);

export const formatTimezoneOffset = (offsetMinutes) => {
  if (!Number.isFinite(offsetMinutes)) return 'UTC+00:00';
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const getDefaultTimezoneOffset = () => {
  if (typeof Date === 'undefined') return 420;
  const offset = -new Date().getTimezoneOffset();
  return Number.isFinite(offset) ? offset : 420;
};
